import { useAuth } from "@clerk/react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { getUnreadCount } from "../../lib/notifications-api";
import { getThreadsUnreadCount } from "../../lib/threads-api";
import { useMessagesStore } from "../../stores/messages";
import { useNotificationsStore } from "../../stores/notifications";

const BADGE_REFRESH_INTERVAL_MS = 45_000;

/**
 * Keeps the app-wide notification and message badge stores current regardless
 * of which authenticated page is mounted. Badge reads are cosmetic and fail
 * silently; their owning inbox surfaces still reconcile counts after actions.
 */
export function DashboardBadgeSync() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    let cancelled = false;
    const sync = async () => {
      const [notifications, messages] = await Promise.allSettled([
        getUnreadCount(getToken),
        getThreadsUnreadCount(getToken),
      ]);
      if (cancelled) return;
      if (notifications.status === "fulfilled") {
        useNotificationsStore
          .getState()
          .setUnreadCount(notifications.value);
      }
      if (messages.status === "fulfilled") {
        useMessagesStore.getState().setUnreadCount(messages.value);
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void sync();
    };

    void sync();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", sync);
    const interval = window.setInterval(
      () => void sync(),
      BADGE_REFRESH_INTERVAL_MS,
    );

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", sync);
      window.clearInterval(interval);
    };
  }, [getToken, isLoaded, isSignedIn, location.pathname]);

  return null;
}
